import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { IconBrandTelegram, IconRefresh, IconSend, IconTrash, IconX } from '@tabler/icons-react';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import AIAgentOrb from 'src/components/AIAgentOrb';
import { DesktopLayout } from 'src/layout';
import { ROUTES_URL } from 'src/constants/routes';
import AIManagersService, {
    AI_MANAGER_GOAL_LABELS,
    AIManagerDisplayKnowledgeProfile,
} from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import { getAIManagerAvatarPalette } from 'src/utils/aiManagers';

import ManagerWorkQueue from './ManagerWorkQueue';
import { TelegramConnectForm } from './TelegramConnectForm';

import type {
    AIManager,
    AIManagerFollowUpRule,
    AIManagerQualificationField,
    AIManagerStatus,
    AIManagerTestDialogMessage,
    AIManagerTestDialogSessionState,
} from 'src/services/AIManagersService';

const CARD_BORDER = 'var(--lm-border)';
const CARD_RADIUS = '8px';
const TEST_WAITING_TITLE = 'Готовим ответ';
const TEST_WAITING_HINT = 'Обычно до минуты';
const cardSx = {
    backgroundColor: 'var(--lm-surface)',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: CARD_RADIUS,
    boxShadow: 'none',
} as const;
const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: CARD_RADIUS,
        bgcolor: 'var(--lm-surface)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: CARD_BORDER,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--lm-text)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--lm-text)',
    },
    '& .MuiInputBase-input::placeholder': {
        color: 'var(--lm-muted)',
        opacity: 1,
    },
} as const;

const statusMeta: Record<
    AIManagerStatus,
    { label: string; color: string; bg: string; orb: 'idle' | 'thinking' | 'building' | 'writing' | 'warning' }
> = {
    running: { label: 'Продаёт', color: 'var(--lm-success-ink)', bg: 'var(--lm-success-bg)', orb: 'writing' },
    paused: { label: 'Пауза', color: 'var(--lm-muted)', bg: 'var(--lm-border)', orb: 'idle' },
    draft: { label: 'Черновик', color: 'var(--lm-accent-ink)', bg: 'var(--lm-accent-soft)', orb: 'building' },
    needs_telegram: {
        label: 'Telegram не подключён',
        color: 'var(--lm-danger-ink)',
        bg: 'var(--lm-danger-bg)',
        orb: 'warning',
    },
    ready_to_launch: {
        label: 'Готов к запуску',
        color: 'var(--lm-accent-ink)',
        bg: 'var(--lm-accent-soft)',
        orb: 'thinking',
    },
    error: { label: 'Ошибка', color: 'var(--lm-danger-ink)', bg: 'var(--lm-danger-bg)', orb: 'warning' },
};

function getManagerStatusMeta(manager: AIManager) {
    if (
        manager.telegramStatus !== 'connected' &&
        (manager.status === 'running' || manager.status === 'paused' || manager.status === 'ready_to_launch')
    ) {
        return statusMeta.needs_telegram;
    }

    return statusMeta[manager.status] || statusMeta.draft;
}

function getTelegramLabel(manager: AIManager) {
    if (manager.telegramStatus === 'connected') {
        return 'Telegram подключён';
    }

    if (manager.telegramStatus === 'warning') {
        return 'Telegram требует проверки';
    }

    return 'Telegram не подключён';
}

function getToggleLabel(manager: AIManager) {
    return manager.status === 'running' ? 'Пауза' : 'Запустить';
}

function getStatusTitle(manager: AIManager) {
    if (manager.telegramStatus !== 'connected') {
        return 'Нужен Telegram';
    }

    if (manager.status === 'ready_to_launch') {
        return 'Готов к запуску';
    }

    if (manager.status === 'running') {
        return 'В работе';
    }

    if (manager.status === 'paused') {
        return 'На паузе';
    }

    return 'Профиль создан';
}

function getStatusText(manager: AIManager) {
    if (manager.telegramStatus !== 'connected') {
        return 'Подключите аккаунт, чтобы менеджер мог писать потенциальным клиентам.';
    }

    if (manager.status === 'ready_to_launch') {
        return 'Проверьте тестовый диалог и запустите менеджера.';
    }

    if (manager.status === 'running') {
        return 'Менеджер может вести диалоги из выбранных потоков.';
    }

    if (manager.status === 'paused') {
        return 'Диалоги не запускаются, пока менеджер на паузе.';
    }

    return 'Проверьте настройки перед запуском.';
}

function getDefaultTestLead(manager: AIManager) {
    if (manager.goal === 'recommend_service') {
        return 'Подскажите хороший Telegram-канал или сервис по этой теме.';
    }

    return 'Ищу подрядчика. Нужно быстро запустить сайт и понять примерный бюджет.';
}

function getRequestErrorMessage(error: unknown, fallback: string) {
    const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    return detail || fallback;
}

function getKnowledgeSourceLabel(value?: string) {
    const source = String(value || '').trim();

    if (!source) {
        return '';
    }

    if (source.startsWith('@') || source.includes('t.me/')) {
        return 'Telegram';
    }

    return 'Сайт';
}

function formatFollowUpDelay(hours: number) {
    const normalized = Math.max(1, Math.round(hours));
    const mod10 = normalized % 10;
    const mod100 = normalized % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return `через ${normalized} час`;
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `через ${normalized} часа`;
    }

    return `через ${normalized} часов`;
}

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1.5}>
                <Typography variant="h3">{title}</Typography>
                {children}
            </Stack>
        </Box>
    );
}

function EmptyText({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            variant="body2"
            color="secondary.main"
        >
            {children}
        </Typography>
    );
}

function TextList({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
    const visibleItems = items.filter(Boolean);

    if (!visibleItems.length) {
        return <EmptyText>{emptyLabel}</EmptyText>;
    }

    return (
        <Stack spacing={0.75}>
            {visibleItems.map((item, index) => (
                <Box
                    key={`${item}-${index}`}
                    display="flex"
                    gap={1}
                    alignItems="flex-start"
                >
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        width={18}
                        flexShrink={0}
                    >
                        {index + 1}
                    </Typography>
                    <Typography variant="body2">{item}</Typography>
                </Box>
            ))}
        </Stack>
    );
}

function hasKnowledgeDisplayProfile(profile: AIManagerDisplayKnowledgeProfile | null) {
    return Boolean(profile?.sections.some((section) => section.items.some((item) => item.text.trim())));
}

function StreamList({ manager }: { manager: AIManager }) {
    if (!manager.leadStreamNames.length) {
        return <EmptyText>Потоки ещё не подключены.</EmptyText>;
    }

    return (
        <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
        >
            {manager.leadStreamNames.map((stream) => (
                <Chip
                    key={stream}
                    label={stream}
                    variant="outlined"
                    sx={{
                        borderColor: CARD_BORDER,
                        borderRadius: CARD_RADIUS,
                        color: 'var(--lm-text)',
                        fontWeight: 600,
                    }}
                />
            ))}
        </Box>
    );
}

function KnowledgeProfileRows({ profile }: { profile: AIManagerDisplayKnowledgeProfile }) {
    const sections = profile.sections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => item.text.trim()),
        }))
        .filter((section) => section.items.length > 0);

    return (
        <Stack spacing={0.5}>
            {sections.map((section, index) => (
                <Box
                    key={section.key}
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: '180px minmax(0, 1fr)' }}
                    gap={1.25}
                    sx={{
                        borderTop: index === 0 ? 'none' : `1px solid ${CARD_BORDER}`,
                        pt: index === 0 ? 0 : 1.25,
                        pb: index === sections.length - 1 ? 0 : 0.75,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 850 }}
                    >
                        {section.title}
                    </Typography>
                    <Stack spacing={0.75}>
                        {section.items.map((item) => (
                            <Typography
                                key={item.id}
                                variant="body2"
                                color="secondary.main"
                                sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                            >
                                {item.text}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            ))}
        </Stack>
    );
}

function KnowledgeBlock({ manager }: { manager: AIManager }) {
    const source = manager.knowledgeSourceUrl || manager.knowledgeSource;
    const sourceLabel = getKnowledgeSourceLabel(source);
    const hasDisplayProfile = hasKnowledgeDisplayProfile(manager.knowledgeDisplayProfile);
    const hasKnowledge = Boolean(source || manager.knowledgeSummary || manager.offerNotes || hasDisplayProfile);

    if (!hasKnowledge) {
        return (
            <DetailBlock title="База знаний">
                <EmptyText>База знаний ещё не заполнена.</EmptyText>
            </DetailBlock>
        );
    }

    return (
        <DetailBlock title="База знаний">
            <Stack spacing={1.25}>
                {source && (
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                    >
                        {sourceLabel && (
                            <Chip
                                label={sourceLabel}
                                size="small"
                                sx={{
                                    bgcolor: 'var(--lm-canvas)',
                                    border: `1px solid ${CARD_BORDER}`,
                                    fontWeight: 700,
                                }}
                            />
                        )}
                        <Typography
                            variant="body2"
                            sx={{ wordBreak: 'break-word' }}
                        >
                            {source}
                        </Typography>
                    </Box>
                )}
                {hasDisplayProfile && manager.knowledgeDisplayProfile ? (
                    <KnowledgeProfileRows profile={manager.knowledgeDisplayProfile} />
                ) : manager.knowledgeSummary ? (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {manager.knowledgeSummary}
                    </Typography>
                ) : null}
                {manager.offerNotes && !hasDisplayProfile && !manager.knowledgeSummary && (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {manager.offerNotes}
                    </Typography>
                )}
            </Stack>
        </DetailBlock>
    );
}

function QualificationRows({ fields }: { fields: AIManagerQualificationField[] }) {
    if (!fields.length) {
        return <EmptyText>Вопросы квалификации ещё не заполнены.</EmptyText>;
    }

    return (
        <Stack spacing={1}>
            {fields.map((field) => (
                <Box
                    key={field.id}
                    sx={{
                        bgcolor: 'var(--lm-canvas)',
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: CARD_RADIUS,
                        p: 1.5,
                    }}
                >
                    <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 800 }}
                        >
                            {field.label}
                        </Typography>
                        {field.required && (
                            <Typography
                                variant="caption"
                                color="secondary.main"
                            >
                                обязательно
                            </Typography>
                        )}
                    </Box>
                    {field.question && (
                        <Typography
                            variant="body2"
                            color="secondary.main"
                            mt={0.5}
                        >
                            {field.question}
                        </Typography>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

function RecommendationRows({ manager }: { manager: AIManager }) {
    if (!manager.recommendationTarget && !manager.recommendationContext) {
        return <EmptyText>Рекомендация ещё не заполнена.</EmptyText>;
    }

    return (
        <Stack spacing={1.25}>
            {manager.recommendationTarget && (
                <Box>
                    <Typography
                        variant="caption"
                        color="secondary.main"
                    >
                        Что рекомендовать
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, wordBreak: 'break-word' }}
                    >
                        {manager.recommendationTarget}
                    </Typography>
                </Box>
            )}
            {manager.recommendationContext && (
                <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                >
                    {manager.recommendationContext}
                </Typography>
            )}
        </Stack>
    );
}

function FollowUpRows({ items }: { items: AIManagerFollowUpRule[] }) {
    if (!items.length) {
        return <EmptyText>Follow-up ещё не настроен.</EmptyText>;
    }

    return (
        <Stack spacing={1}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{
                        bgcolor: 'var(--lm-canvas)',
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: CARD_RADIUS,
                        p: 1.5,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 800 }}
                    >
                        {item.reason || 'Триггер'}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="secondary.main"
                    >
                        {formatFollowUpDelay(item.delayHours)}
                    </Typography>
                    {item.message && (
                        <Typography
                            variant="body2"
                            mt={0.75}
                        >
                            {item.message}
                        </Typography>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

function FirstMessageBlock({ manager }: { manager: AIManager }) {
    return (
        <DetailBlock title="Первое сообщение">
            <Box
                sx={{
                    bgcolor: 'var(--lm-canvas)',
                    border: `1px solid ${CARD_BORDER}`,
                    borderRadius: CARD_RADIUS,
                    p: 1.5,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                >
                    {manager.firstMessage || 'Первое сообщение соберётся из цели и контекста потенциального клиента.'}
                </Typography>
            </Box>
        </DetailBlock>
    );
}

function StatusBlock({ manager }: { manager: AIManager }) {
    return (
        <DetailBlock title={getStatusTitle(manager)}>
            <Typography
                variant="body2"
                color="secondary.main"
            >
                {getStatusText(manager)}
            </Typography>
        </DetailBlock>
    );
}

function useTestDialogState(manager: AIManager) {
    const [messages, setMessages] = useState<AIManagerTestDialogMessage[]>([]);
    const [input, setInput] = useState(() => getDefaultTestLead(manager));
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [sessionState, setSessionState] = useState<AIManagerTestDialogSessionState | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [missingFacts, setMissingFacts] = useState<string[]>([]);

    return {
        messages,
        setMessages,
        input,
        setInput,
        sessionId,
        setSessionId,
        sessionState,
        setSessionState,
        isSending,
        setIsSending,
        error,
        setError,
        missingFacts,
        setMissingFacts,
    };
}

const TestDialogContext = createContext<ReturnType<typeof useTestDialogState> | null>(null);

function ManagerTestDialogProvider({ manager, children }: { manager: AIManager; children: React.ReactNode }) {
    const state = useTestDialogState(manager);
    return <TestDialogContext.Provider value={state}>{children}</TestDialogContext.Provider>;
}

function ManagerTestDialogBlock({ manager }: { manager: AIManager }) {
    const testState = useContext(TestDialogContext);
    if (!testState) throw new Error('Test dialog requires ManagerTestDialogProvider');
    const {
        messages,
        setMessages,
        input,
        setInput,
        sessionId,
        setSessionId,
        sessionState,
        setSessionState,
        isSending,
        setIsSending,
        error,
        setError,
        missingFacts,
        setMissingFacts,
    } = testState;

    const handleReset = () => {
        setMessages([]);
        setInput(getDefaultTestLead(manager));
        setSessionId(null);
        setSessionState(null);
        setError('');
        setMissingFacts([]);
    };

    const handleSend = async () => {
        const text = input.trim();

        if (!text || isSending) {
            return;
        }

        const userMessage: AIManagerTestDialogMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: text,
        };
        const nextMessages = [...messages, userMessage];

        setIsSending(true);
        setError('');

        try {
            const result = await AIManagersService.testDialog({
                managerId: manager.id,
                messages: nextMessages,
                sessionId,
                sessionState,
            });

            setMessages([...nextMessages, result.assistantMessage]);
            setSessionId(result.sessionId);
            setSessionState(result.sessionState);
            setMissingFacts(result.missingFacts);
            setInput('');
        } catch (requestError) {
            setError(getRequestErrorMessage(requestError, 'Не удалось получить ответ движка.'));
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1.5}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1.5}
                >
                    <Box>
                        <Typography variant="h3">Тестовый диалог</Typography>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                            mt={0.25}
                        >
                            Ответ от движка AI-менеджера
                        </Typography>
                    </Box>
                    <Button
                        variant="text"
                        onClick={handleReset}
                        disabled={isSending}
                        sx={{
                            color: 'var(--lm-muted)',
                            minWidth: 36,
                            px: 1,
                        }}
                        aria-label="Сбросить тест"
                    >
                        <IconRefresh size={18} />
                    </Button>
                </Box>

                <Box
                    sx={{
                        bgcolor: 'var(--lm-canvas)',
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: CARD_RADIUS,
                        minHeight: 160,
                        p: 1.5,
                    }}
                >
                    {messages.length === 0 ? (
                        <EmptyText>Введите сообщение потенциального клиента и нажмите “Проверить”.</EmptyText>
                    ) : (
                        <Stack spacing={1.25}>
                            {messages.map((message, index) => (
                                <Box
                                    key={message.id || `${message.role}-${index}`}
                                    alignSelf={message.role === 'assistant' ? 'flex-end' : 'flex-start'}
                                    sx={{
                                        bgcolor:
                                            message.role === 'assistant'
                                                ? 'var(--lm-accent-soft)'
                                                : 'var(--lm-surface)',
                                        border: `1px solid ${
                                            message.role === 'assistant' ? 'var(--lm-accent-border)' : CARD_BORDER
                                        }`,
                                        borderRadius: CARD_RADIUS,
                                        maxWidth: '88%',
                                        p: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ whiteSpace: 'pre-line' }}
                                    >
                                        {message.content}
                                    </Typography>
                                </Box>
                            ))}
                            {isSending && (
                                <Box
                                    alignSelf="flex-end"
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{
                                        bgcolor: 'var(--lm-accent-soft)',
                                        border: '1px solid var(--lm-accent-border)',
                                        borderRadius: CARD_RADIUS,
                                        p: 1.5,
                                    }}
                                >
                                    <CircularProgress
                                        size={14}
                                        sx={{ flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            {TEST_WAITING_TITLE}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="secondary.main"
                                        >
                                            {TEST_WAITING_HINT}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    )}
                </Box>

                {missingFacts.length > 0 && (
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={0.75}
                    >
                        {missingFacts.slice(0, 4).map((fact) => (
                            <Chip
                                key={fact}
                                size="small"
                                label={`Уточнить: ${fact}`}
                                sx={{ bgcolor: 'var(--lm-canvas)', border: `1px solid ${CARD_BORDER}` }}
                            />
                        ))}
                    </Box>
                )}

                {error && (
                    <Typography
                        variant="body2"
                        color="var(--lm-danger-ink)"
                    >
                        {error}
                    </Typography>
                )}

                <Box
                    component="form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleSend();
                    }}
                    sx={{
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: CARD_RADIUS,
                        overflow: 'hidden',
                    }}
                >
                    <TextField
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        placeholder="Сообщение потенциального клиента"
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{
                            ...textFieldSx,
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '& textarea': {
                                fontSize: 14,
                                lineHeight: '20px',
                            },
                        }}
                    />
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        px={1}
                        pb={1}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSending || !input.trim()}
                            startIcon={
                                isSending ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <IconSend size={16} />
                                )
                            }
                            sx={{
                                borderRadius: CARD_RADIUS,
                                textTransform: 'none',
                            }}
                        >
                            Проверить
                        </Button>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
}

function ManagerRightSidebar({
    manager,
    isUpdating,
    isDeleting,
    statusError,
    deleteError,
    onToggleStatus,
    onTelegramConnected,
    onDelete,
}: {
    manager: AIManager;
    isUpdating: boolean;
    isDeleting: boolean;
    statusError: string;
    deleteError: string;
    onToggleStatus: () => void;
    onTelegramConnected: () => void;
    onDelete: () => void;
}) {
    const [showTelegramForm, setShowTelegramForm] = useState(false);
    const history = useHistory();
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const meta = getManagerStatusMeta(manager);
    const palette = getAIManagerAvatarPalette(manager.avatarPaletteId);
    const telegramLabel = getTelegramLabel(manager);
    const sidebarCaption =
        manager.nextStep && manager.nextStep !== meta.label && manager.nextStep !== telegramLabel
            ? manager.nextStep
            : AI_MANAGER_GOAL_LABELS[manager.goal] || manager.role;
    const canToggle =
        manager.status === 'running' ||
        (manager.telegramStatus === 'connected' &&
            (manager.status === 'paused' || manager.status === 'ready_to_launch'));
    const needsTelegram = manager.status === 'needs_telegram' || manager.telegramStatus !== 'connected';
    const handleTelegramConnected = () => {
        setShowTelegramForm(false);
        onTelegramConnected();
    };

    useEffect(() => {
        setIsConfirmingDelete(false);
    }, [manager.id]);

    return (
        <Box
            sx={{
                height: '100%',
                p: '1rem 1rem 1rem 0',
                width: '100%',
            }}
        >
            <Stack spacing={2}>
                <Paper
                    elevation={0}
                    sx={{ ...cardSx, p: 2.5 }}
                >
                    <Stack
                        alignItems="center"
                        spacing={2}
                        textAlign="center"
                    >
                        <AIAgentOrb
                            size={112}
                            state={meta.orb}
                            palette={palette}
                        />
                        <Box>
                            <Typography variant="h3">{manager.name}</Typography>
                            <Typography
                                mt={0.5}
                                variant="body2"
                                color="secondary.main"
                            >
                                {sidebarCaption}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                            justifyContent="center"
                        >
                            <Chip
                                label={meta.label}
                                sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }}
                            />
                            {telegramLabel !== meta.label && (
                                <Chip
                                    label={telegramLabel}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        {statusError && (
                            <Typography
                                variant="body2"
                                color="var(--lm-danger-ink)"
                            >
                                {statusError}
                            </Typography>
                        )}
                        {canToggle && (
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={isUpdating || isDeleting}
                                onClick={onToggleStatus}
                            >
                                {isUpdating ? 'Обновляю...' : getToggleLabel(manager)}
                            </Button>
                        )}
                        {needsTelegram && !canToggle && (
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={isDeleting}
                                onClick={() => setShowTelegramForm((value) => !value)}
                            >
                                Подключить Telegram
                            </Button>
                        )}
                        {showTelegramForm && needsTelegram && (
                            <TelegramConnectForm
                                managerId={manager.id}
                                service={AIManagersService}
                                onConnected={handleTelegramConnected}
                            />
                        )}
                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={isDeleting}
                            onClick={() =>
                                history.push(`${ROUTES_URL.SALES_FUNNEL}?managerId=${encodeURIComponent(manager.id)}`)
                            }
                        >
                            Воронка продаж
                        </Button>
                        <Box
                            sx={{
                                width: '100%',
                                pt: 1,
                                borderTop: `1px solid ${CARD_BORDER}`,
                            }}
                        >
                            {isConfirmingDelete ? (
                                <Stack spacing={1}>
                                    <Typography
                                        variant="caption"
                                        color="secondary.main"
                                    >
                                        Менеджер будет удалён без восстановления
                                    </Typography>
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="1fr 1fr"
                                        gap={1}
                                    >
                                        <Button
                                            variant="contained"
                                            disabled={isDeleting}
                                            onClick={onDelete}
                                            startIcon={
                                                isDeleting ? (
                                                    <CircularProgress
                                                        size={14}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <IconTrash size={16} />
                                                )
                                            }
                                            sx={{
                                                bgcolor: 'var(--lm-danger-ink)',
                                                '&:hover': { bgcolor: '#C84735' },
                                            }}
                                        >
                                            Удалить
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            disabled={isDeleting}
                                            onClick={() => setIsConfirmingDelete(false)}
                                            startIcon={<IconX size={16} />}
                                        >
                                            Отмена
                                        </Button>
                                    </Box>
                                </Stack>
                            ) : (
                                <Button
                                    variant="text"
                                    fullWidth
                                    disabled={isDeleting}
                                    onClick={() => setIsConfirmingDelete(true)}
                                    startIcon={<IconTrash size={16} />}
                                    sx={{
                                        color: 'var(--lm-danger-ink)',
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        '&:hover': { bgcolor: 'var(--lm-danger-bg)' },
                                    }}
                                >
                                    Удалить менеджера
                                </Button>
                            )}
                            {deleteError && (
                                <Typography
                                    variant="body2"
                                    color="var(--lm-danger-ink)"
                                    mt={1}
                                >
                                    {deleteError}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    );
}

export default function AIManagerDetailsScreen() {
    const { managerId } = useParams<{ managerId: string }>();
    return (
        <ManagerDetails
            key={managerId}
            managerId={managerId}
        />
    );
}

function ManagerDetails({ managerId }: { managerId: string }) {
    const history = useHistory();
    const location = useLocation();
    const activeTab =
        location.hash === '#knowledge' ? 'knowledge' : location.hash === '#settings' ? 'settings' : 'work';
    const requestVersion = useRef(0);
    const mounted = useRef(true);
    const { enqueueSnackbar } = useSnackbar();
    const [manager, setManager] = useState<AIManager | null>(() => {
        return AIManagersService.getManagersSync().find((item) => item.id === managerId) || null;
    });
    const [isLoading, setIsLoading] = useState(!manager);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [statusError, setStatusError] = useState('');
    const [deleteError, setDeleteError] = useState('');

    const loadManager = useCallback(async () => {
        if (!managerId) {
            return;
        }

        const version = ++requestVersion.current;
        setIsLoading(true);
        setLoadError('');

        try {
            const loaded = await AIManagersService.getManager(managerId);
            if (version === requestVersion.current) setManager(loaded);
        } catch (_error) {
            if (version === requestVersion.current) setLoadError('Не удалось загрузить ИИ-менеджера.');
        } finally {
            if (version === requestVersion.current) setIsLoading(false);
        }
    }, [managerId]);

    useEffect(() => {
        mounted.current = true;
        void loadManager();

        const subscriber = EventService.subscribe<AIManager>('AI_MANAGERS_UPDATED', (_eventType, updatedManager) => {
            if (updatedManager && updatedManager.id === managerId) {
                requestVersion.current++;
                setManager(updatedManager);
                setLoadError('');
                setIsLoading(false);
                return;
            }

            void loadManager();
        });

        return () => {
            mounted.current = false;
            requestVersion.current++;
            subscriber.unsubscribe();
        };
    }, [loadManager, managerId]);

    const handleToggleStatus = useCallback(async () => {
        if (!manager) {
            return;
        }

        setIsUpdatingStatus(true);
        setStatusError('');

        try {
            const nextStatus = manager.status === 'running' ? 'paused' : 'running';
            const updated = await AIManagersService.updateManagerStatus(manager.id, nextStatus);
            if (!mounted.current) return;
            setManager(updated);
            EventService.dispatch('AI_MANAGERS_UPDATED', updated);
        } catch (error) {
            if (!mounted.current) return;
            const updated = (error as { manager?: AIManager }).manager;
            if (updated) {
                setManager(updated);
                EventService.dispatch('AI_MANAGERS_UPDATED', updated);
            }
            setStatusError(
                updated?.telegramStatus === 'not_connected'
                    ? 'Для запуска нужно подключить Telegram.'
                    : 'Не удалось изменить состояние менеджера. Попробуйте ещё раз.'
            );
        } finally {
            if (mounted.current) setIsUpdatingStatus(false);
        }
    }, [manager]);

    const handleDeleteManager = useCallback(async () => {
        if (!manager || isDeleting) {
            return;
        }

        setIsDeleting(true);
        setDeleteError('');

        try {
            await AIManagersService.deleteManager(manager.id);
            EventService.dispatch('AI_MANAGERS_UPDATED');
            if (!mounted.current) return;
            enqueueSnackbar('AI-менеджер удалён', { variant: 'success' });
            history.push(ROUTES_URL.AI_MANAGER_NEW);
        } catch (_error) {
            if (mounted.current) setDeleteError('Не удалось удалить AI-менеджера');
        } finally {
            if (mounted.current) setIsDeleting(false);
        }
    }, [enqueueSnackbar, isDeleting, manager]);

    const visibleManager = useMemo(() => manager, [manager]);

    if (isLoading && !visibleManager) {
        return (
            <DesktopLayout hideRightSidebar>
                <Box
                    px={{ xs: 2, md: 0 }}
                    py={{ xs: 2, md: 0 }}
                >
                    <Paper sx={{ ...cardSx, p: 3 }}>
                        <EmptyText>Загружаем AI-менеджера...</EmptyText>
                    </Paper>
                </Box>
            </DesktopLayout>
        );
    }

    if (!visibleManager) {
        return (
            <DesktopLayout hideRightSidebar>
                <Box
                    px={{ xs: 2, md: 0 }}
                    py={{ xs: 2, md: 0 }}
                >
                    <Paper sx={{ ...cardSx, p: 3 }}>
                        <Stack spacing={2}>
                            <Typography variant="h2">
                                {loadError ? 'Не удалось загрузить менеджера' : 'ИИ-менеджер не найден'}
                            </Typography>
                            <EmptyText>{loadError || 'Возможно, он был удалён или ещё не создан.'}</EmptyText>
                            <Button
                                variant="contained"
                                sx={{ alignSelf: 'flex-start' }}
                                onClick={() =>
                                    loadError ? void loadManager() : history.push(ROUTES_URL.AI_MANAGER_NEW)
                                }
                            >
                                {loadError ? 'Повторить' : 'Новый ИИ-менеджер'}
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            </DesktopLayout>
        );
    }

    const goalLabel = AI_MANAGER_GOAL_LABELS[visibleManager.goal] || visibleManager.role;
    const goalDescription = visibleManager.goalDescription.trim();
    const hasCustomGoalDescription = Boolean(goalDescription && goalDescription !== goalLabel);
    const rightSidebar = (
        <ManagerRightSidebar
            manager={visibleManager}
            isUpdating={isUpdatingStatus}
            isDeleting={isDeleting}
            statusError={statusError}
            deleteError={deleteError}
            onToggleStatus={handleToggleStatus}
            onTelegramConnected={() => {
                void loadManager();
            }}
            onDelete={handleDeleteManager}
        />
    );

    const content = (
        <Box
            display="flex"
            flexDirection="column"
            gap={3}
            px={{ xs: 2, md: 0 }}
            py={{ xs: 2, md: 0 }}
        >
            <Box>
                <Typography variant="h1">{visibleManager.name}</Typography>
                <Typography
                    mt={0.5}
                    variant="body2"
                    color="secondary.main"
                >
                    {goalLabel}
                </Typography>
            </Box>

            {loadError && (
                <Box role="alert">
                    <Typography variant="body2">Не удалось обновить данные менеджера.</Typography>
                    <Button
                        disabled={isLoading}
                        onClick={() => {
                            void loadManager();
                        }}
                    >
                        Повторить
                    </Button>
                </Box>
            )}

            <Tabs
                value={activeTab}
                aria-label="Разделы ИИ-менеджера"
                onChange={(_event, value) => history.push({ ...location, hash: `#${value}` })}
                sx={{
                    borderBottom: '1px solid var(--lm-border)',
                    minHeight: 44,
                    '& .MuiTab-root': { minHeight: 44, minWidth: 0, px: { xs: 1.5, sm: 2 }, textTransform: 'none' },
                }}
            >
                <Tab
                    value="work"
                    label="Работа"
                    id="manager-tab-work"
                    aria-controls="manager-panel-work"
                />
                <Tab
                    value="knowledge"
                    label="База знаний"
                    id="manager-tab-knowledge"
                    aria-controls="manager-panel-knowledge"
                />
                <Tab
                    value="settings"
                    label="Настройки"
                    id="manager-tab-settings"
                    aria-controls="manager-panel-settings"
                />
            </Tabs>

            <Box
                role="tabpanel"
                id="manager-panel-work"
                aria-labelledby="manager-tab-work"
                hidden={activeTab !== 'work'}
            >
                <ManagerWorkQueue
                    managerId={managerId}
                    active={activeTab === 'work'}
                />
            </Box>

            <Box
                role="tabpanel"
                id="manager-panel-knowledge"
                aria-labelledby="manager-tab-knowledge"
                hidden={activeTab !== 'knowledge'}
            >
                <Stack spacing={3}>
                    <KnowledgeBlock manager={visibleManager} />
                    <ManagerTestDialogBlock
                        key={`${visibleManager.id}:${visibleManager.goal}`}
                        manager={visibleManager}
                    />
                </Stack>
            </Box>

            <Box
                role="tabpanel"
                id="manager-panel-settings"
                aria-labelledby="manager-tab-settings"
                hidden={activeTab !== 'settings'}
            >
                <Stack spacing={3}>
                    <StatusBlock manager={visibleManager} />

                    <DetailBlock title="Цель">
                        <Stack spacing={1}>
                            <Typography variant="body1">{goalLabel}</Typography>
                            {hasCustomGoalDescription ? (
                                <Typography
                                    variant="body2"
                                    color="secondary.main"
                                >
                                    {goalDescription}
                                </Typography>
                            ) : (
                                <EmptyText>Цель диалога ещё не описана.</EmptyText>
                            )}
                        </Stack>
                    </DetailBlock>

                    <DetailBlock title="Потоки лидов">
                        <StreamList manager={visibleManager} />
                    </DetailBlock>

                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={1.5}
                    >
                        <DetailBlock
                            title={
                                visibleManager.goal === 'qualify'
                                    ? 'Квалификация потенциального клиента'
                                    : 'Рекомендация'
                            }
                        >
                            {visibleManager.goal === 'qualify' ? (
                                <QualificationRows fields={visibleManager.qualificationFields} />
                            ) : (
                                <RecommendationRows manager={visibleManager} />
                            )}
                        </DetailBlock>
                        <DetailBlock title="Сценарий">
                            <TextList
                                items={visibleManager.flowSteps}
                                emptyLabel="Сценарий ещё не заполнен."
                            />
                        </DetailBlock>
                    </Box>

                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={1.5}
                    >
                        <FirstMessageBlock manager={visibleManager} />
                        <DetailBlock title="Follow-up">
                            <FollowUpRows items={visibleManager.followUps} />
                        </DetailBlock>
                    </Box>

                    {visibleManager.handoffRule && (
                        <DetailBlock title="Передача человеку">
                            <Typography variant="body2">{visibleManager.handoffRule}</Typography>
                        </DetailBlock>
                    )}

                    {visibleManager.stopPhrases.length > 0 && (
                        <DetailBlock title="Стоп-фразы">
                            <TextList
                                items={visibleManager.stopPhrases}
                                emptyLabel="Стоп-фразы ещё не заполнены."
                            />
                        </DetailBlock>
                    )}
                </Stack>
            </Box>
        </Box>
    );

    return (
        <ManagerTestDialogProvider
            key={`${visibleManager.id}:${visibleManager.goal}`}
            manager={visibleManager}
        >
            <DesktopLayout
                rightSidebar={activeTab === 'settings' ? rightSidebar : undefined}
                hideRightSidebar={activeTab !== 'settings'}
                rightSidebarTitle="Управление менеджером"
            >
                {content}
            </DesktopLayout>
        </ManagerTestDialogProvider>
    );
}
